/**
 * The game's version and its patch notes, both read from CHANGELOG.md at the root of the repository: one list
 * for the player and for whoever ships the next release. The newest entry IS the version shown on the title
 * screen, and a test holds package.json's version to it, so a release cannot raise one without the other.
 * Asked for in play (2026-09-23): the corner said V0.0.1 through every change made to the game.
 */

import source from '../../../../CHANGELOG.md?raw';

export interface Release {
  /** "0.7.4" */
  version: string;
  /** "2026-09-23" */
  date: string;
  /** One line per change, in the player's terms. */
  changes: string[];
}

/** `## 0.7.4 - 2026-09-23` headings, then `- ` lines under each. Anything else is commentary and is skipped. */
export function parseChangelog(text: string): Release[] {
  const out: Release[] = [];
  let current: Release | null = null;
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    const head = /^##\s+(\d+\.\d+\.\d+)\s+-\s+(\d{4}-\d{2}-\d{2})\s*$/.exec(line);
    if (head) {
      current = { version: head[1] ?? '', date: head[2] ?? '', changes: [] };
      out.push(current);
    } else if (current !== null && line.startsWith('- ')) {
      current.changes.push(line.slice(2).trim());
    }
  }
  return out;
}

export const RELEASES: readonly Release[] = parseChangelog(source);

/** The version this build is: the newest entry. */
export const CURRENT_VERSION = RELEASES[0]?.version ?? '0.0.0';

export const CHANGES_SEEN_KEY = 'nightdrop.changesSeen';

/**
 * Has this browser opened the patch notes since the current version came out? Kept out of the profile save,
 * like the training flag: it is about this browser's player. Guarded - a blocked store just never remembers.
 */
export function changesSeen(): boolean {
  try {
    return window.localStorage.getItem(CHANGES_SEEN_KEY) === CURRENT_VERSION;
  } catch {
    return true;
  }
}

export function markChangesSeen(): void {
  try {
    window.localStorage.setItem(CHANGES_SEEN_KEY, CURRENT_VERSION);
  } catch {
    // Not remembered: the corner keeps saying NEW, which is harmless.
  }
}
